import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { Card, Metric, Text } from '@tremor/react';


const apiUrlSeleccionMensual = process.env.REACT_APP_API_PEDIDOSMENSUALES;

const GraficosMensuales = ({ mes, sedeId }) => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        let url = apiUrlSeleccionMensual;
        if (mes) {
          url += `?mes=${mes}`;
        }
        const res = await axios.get(url);
        console.log('Datos mensuales cargados:', res.data);
        
        // Filtrar por sede si se seleccionó una
        let datosFiltrados = res.data || [];
        if (sedeId && sedeId !== 'todas') {
          datosFiltrados = datosFiltrados.filter(p => p.sede_id === sedeId);
        }
        
        setDatos(datosFiltrados);
      } catch (err) {
        console.error('Error al cargar datos mensuales:', err);
        setError(err.message);
      } finally {
        setCargando(false);
      }
    };
    obtenerDatos();
  }, [mes, sedeId]);

  const meses = {};
  const platosContador = {};
  const tiposDeMenu = new Set(); // Detectar tipos dinámicamente

  datos.forEach((pedido) => {
    const fecha = new Date(pedido.fecha);
    const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
    const tipo = pedido.tipo || 'Sin tipo';
    
    tiposDeMenu.add(tipo);

    if (!meses[key]) {
      meses[key] = {};
    }
    
    if (!meses[key][tipo]) {
      meses[key][tipo] = 0;
    }
    
    meses[key][tipo]++;

    // Contador por tipo de plato
    const nombrePlato = pedido.nombre;
    if (nombrePlato) {
      platosContador[nombrePlato] = (platosContador[nombrePlato] || 0) + 1;
    }
  });
  
  const tiposArray = Array.from(tiposDeMenu);
  const categorias = Object.keys(meses).map((mes) =>
    new Date(mes + '-01T00:00').toLocaleDateString('es-ES', {
      month: 'short',
      year: 'numeric',
    })
  );

  // Generar series dinámicamente para cada tipo
  const seriesBar = tiposArray.map(tipo => ({
    name: tipo,
    data: Object.keys(meses).map(key => meses[key][tipo] || 0)
  }));

  // Total por tipo
  const totalPorTipo = {};
  tiposArray.forEach(tipo => {
    totalPorTipo[tipo] = Object.values(meses).reduce((sum, mes) => sum + (mes[tipo] || 0), 0);
  });

  const seriesPie = tiposArray.map(tipo => totalPorTipo[tipo]);

  const optionsBar = {
    chart: {
      id: 'bar-cantidades',
      foreColor: '#fff',
      stacked: false, // 👈 desactivamos stacked
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
      },
    },
    dataLabels: {
      enabled: true,
      style: { colors: ['#fff'] },
    },
    xaxis: {
      categories: categorias,
      labels: { style: { colors: '#fff' } },
    },
    yaxis: {
      labels: { style: { colors: '#fff' } },
    },
    legend: {
      labels: { colors: '#fff' },
      position: 'bottom',
    },
    tooltip: {
      theme: 'dark',
    },
  };



  const optionsPie = {
    labels: tiposArray,
    legend: {
      position: 'bottom',
      labels: { colors: ['#fff'] },
    },
    dataLabels: {
      style: {
        colors: ['#fff'],
      },
    },
    chart: {
      foreColor: '#fff',
    },
    tooltip: {
      theme: 'dark', // 👈 esto es lo nuevo
    },
  };

  // 🍽️ Top platos con gráfico horizontal
  const topPlatos = Object.entries(platosContador)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const platosLabels = topPlatos.map(([plato]) => plato);
  const platosCantidades = topPlatos.map(([_, cantidad]) => cantidad);

  const optionsHorizontal = {
    chart: {
      type: 'bar',
      foreColor: '#fff',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '60%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: platosLabels,
      labels: {
        style: { colors: '#fff' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: '#fff' },
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'dark', // 👈 esto es lo nuevo
    },
  };

  const seriesHorizontal = [
    {
      name: 'Pedidos',
      data: platosCantidades,
    },
  ];

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      {cargando && <p className="text-white lg:col-span-12">Cargando datos...</p>}
      {error && <p className="text-red-500 lg:col-span-12">Error: {error}</p>}
      {!cargando && datos.length === 0 && <p className="text-gray-400 lg:col-span-12">No hay datos disponibles</p>}

      {/* Botones de acceso removidos por requerimiento */}
      {/* Totales */}
      <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 text-white shadow-xl">
          <Metric>Total pedidos</Metric>
          <Text>{Object.values(totalPorTipo).reduce((sum, val) => sum + val, 0)}</Text>
        </Card>
        {tiposArray.slice(0, 3).map((tipo, index) => (
          <Card key={index} className="bg-slate-900 text-white shadow-xl">
            <Metric>{tipo}</Metric>
            <Text>{totalPorTipo[tipo] || 0}</Text>
          </Card>
        ))}
      </div>

      {/* Gráfico de barras por mes */}
      <div className="lg:col-span-8">
        <Card className="p-4 bg-slate-900 text-white rounded-xl h-full shadow-xl">
          <Metric className="mb-4">Reservas por tipo (mensual)</Metric>
          <Chart options={optionsBar} series={seriesBar} type="bar" height={350} />
        </Card>
      </div>

      {/* Ranking de platos */}
      <div className="lg:col-span-4">
        <Card className="p-4 bg-slate-900 text-white h-full shadow-xl">
          <Metric className="mb-4">Platos más solicitados</Metric>
          <Chart
            options={optionsHorizontal}
            series={seriesHorizontal}
            type="bar"
            height={60 * topPlatos.length + 50}
          />
        </Card>
      </div>

      {/* Gráfico de torta */}
      <div className="lg:col-span-6">
        <Card className="p-4 bg-slate-900 text-white rounded-xl shadow-xl">
          <Metric className="mb-4">Proporción total por tipo</Metric>
          <Chart options={optionsPie} series={seriesPie} type="pie" height={350} />
        </Card>
      </div>
    </div>
  );
};

export default GraficosMensuales;



